/**
 * Token Expiration Warning Modal Component
 * Shows warning when access token is about to expire
 * Similar to IdleWarningModal but for token expiration
 */

import React, { useEffect, useRef, useCallback } from 'react';
import useRealTimeCountdown from '../hooks/useRealTimeCountdown';

export default function TokenExpirationWarningModal({ open, expiresAtMs, onRefresh, onDismiss }) {
  const seconds = useRealTimeCountdown({ 
    endAtMs: expiresAtMs, 
    onEnd: onRefresh, 
    visibleUpdateMs: 250, 
    hiddenUpdateMs: 1000 
  });

  const dialogRef = useRef(null);
  const firstBtnRef = useRef(null);

  // Scroll lock when open
  useEffect(() => {
    if (!open) return;
    
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevTouch = body.style.touchAction;
    const prevScroll = body.style.overscrollBehavior;
    
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    body.style.overscrollBehavior = 'contain';
    
    return () => { 
      body.style.overflow = prevOverflow; 
      body.style.touchAction = prevTouch; 
      body.style.overscrollBehavior = prevScroll; 
    };
  }, [open]);

  // Focus trap + initial focus
  useEffect(() => {
    if (open) {
      const to = setTimeout(() => {
        firstBtnRef.current?.focus();
      }, 0);
      return () => clearTimeout(to);
    }
  }, [open]);

  // Handle escape key
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && open) {
      // Don't allow dismissing with Escape for security
      e.preventDefault();
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="token-exp-title"
      aria-describedby="token-exp-desc"
      className="token-exp-modal-container"
      onKeyDown={onKeyDown}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
    >
      <div 
        className="token-exp-modal-backdrop" 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'var(--background-overlay)',
          backdropFilter: 'blur(4px)'
        }}
      />
      <div 
        className="token-exp-modal-positioner"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '420px',
          zIndex: 1
        }}
      >
        <div
          ref={dialogRef}
          className="token-exp-modal-content"
          style={{
            background: 'var(--background-primary)',
            borderRadius: 0,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div className="token-exp-modal-header" style={{ padding: '1.5rem', borderBottom: '1px solid var(--border-color)' }}>
            <div className="token-exp-modal-icon" style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: 0, 
              background: 'linear-gradient(135deg, var(--warning-color) 0%, var(--warning-dark) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1rem'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="token-exp-modal-title-section">
              <h2 id="token-exp-title" className="token-exp-modal-title" style={{ 
                fontSize: '1.25rem', 
                fontWeight: 600, 
                color: 'var(--text-primary)',
                margin: 0,
                marginBottom: '0.5rem'
              }}>
                Session Expiring Soon
              </h2>
              <p className="token-exp-modal-subtitle" style={{ 
                fontSize: '0.875rem', 
                color: 'var(--text-muted)',
                margin: 0
              }}>
                Your access token will expire in <strong style={{ color: 'var(--warning-color)' }}>{seconds}</strong> seconds
              </p>
            </div>
          </div>

          <div className="token-exp-modal-body" style={{ padding: '1.5rem' }}>
            <p id="token-exp-desc" className="token-exp-modal-description" style={{ 
              fontSize: '0.9375rem', 
              color: 'var(--text-secondary)',
              lineHeight: '1.6',
              margin: 0,
              marginBottom: '1rem'
            }}>
              To continue your session, please refresh your token. This will extend your session automatically.
            </p>

            <div className="token-exp-countdown-container" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem',
              background: 'rgba(246, 224, 94, 0.1)',
              borderRadius: 0,
              marginBottom: '1rem'
            }}>
              <div className="token-exp-countdown-icon" style={{
                width: '32px',
                height: '32px',
                borderRadius: 0,
                background: 'var(--warning-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div className="token-exp-countdown-text-section">
                <div className="token-exp-countdown-label" style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--warning-dark)',
                  fontWeight: 500,
                  marginBottom: '0.25rem'
                }}>
                  Time remaining
                </div>
                <div className="token-exp-countdown-value" style={{ 
                  fontSize: '1.125rem', 
                  fontWeight: 600, 
                  color: 'var(--warning-dark)'
                }}>
                  {seconds} seconds
                </div>
              </div>
            </div>
          </div>

          <div className="token-exp-modal-actions" style={{ 
            padding: '1.5rem', 
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'flex-end'
          }}>
            <button
              ref={firstBtnRef}
              onClick={onRefresh}
              className="token-exp-btn token-exp-btn-primary"
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: 0,
                border: 'none',
                background: 'linear-gradient(135deg, var(--warning-color) 0%, var(--warning-dark) 100%)',
                color: 'var(--text-inverse)',
                fontWeight: 600,
                fontSize: '0.9375rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
                minHeight: '44px'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-1px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Refresh Token
            </button>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="token-exp-btn token-exp-btn-secondary"
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: 0,
                  border: '1px solid var(--border-color)',
                  background: 'var(--background-primary)',
                  color: 'var(--text-primary)',
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minHeight: '44px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--background-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'var(--background-primary)';
                }}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

